import type { ComponentProps, ComponentType } from 'react';
import { useRef, useState } from 'react';
import { MultiSelect } from 'react-multi-select-component';

/** The library supports `onMenuToggle`, `ArrowRenderer`, and `ItemRenderer` at runtime but omits them from its published types — add them back. */
type MultiSelectProps = ComponentProps<typeof MultiSelect> & {
  onMenuToggle?: (open: boolean) => void;
  ArrowRenderer?: ComponentType<{ expanded: boolean }>;
  ItemRenderer?: ComponentType<{
    checked: boolean;
    option: { label: string; value: unknown };
    onClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
  }>;
};

/** MultiSelect retyped to accept the `onMenuToggle` prop it actually supports. */
const MultiSelectWithToggle = MultiSelect as unknown as ComponentType<MultiSelectProps>;

/**
 * Bombiix icon-font chevron used in place of rmsc's default SVG arrow, so multi-select
 * cells match the react-select master-design (CellReactSelect / NewSelectMultiSelect).
 */
function BombiixArrow() {
  return <i className="bombiix-icon-chevron-down" aria-hidden="true" style={{ fontSize: 16, color: '#a4a4a4', lineHeight: 1 }} />;
}

/**
 * Replaces rmsc's default option row with the Bombiix master-design 16px checkbox
 * (`.bombiix-checkbox--sm`) so multi-select rows match every other checkbox in the app.
 * The wrapper is a `<span>` — not a `<label>` — because rmsc already wraps the row
 * in `<label class="select-item">`; nesting a second label would double-fire clicks.
 */
function BombiixItemRenderer({
  checked,
  option,
  onClick,
  disabled,
}: {
  checked: boolean;
  option: { label: string; value: unknown };
  onClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}) {
  return (
    <div className="item-renderer">
      <span className="bombiix-checkbox bombiix-checkbox--sm">
        <input type="checkbox" className="bombiix-checkbox__input" checked={checked} onChange={onClick} disabled={disabled} tabIndex={-1} />
        <span className="bombiix-checkbox__box" aria-hidden="true" />
      </span>
      <span className="mssg-ms-item-label">{option.label}</span>
    </div>
  );
}

/** Rough per-option row height (28px control + checkbox rows) used to estimate menu height for flip decisions. */
const OPTION_ROW = 34;
const MENU_CHROME = 24;
const MAX_VISIBLE_OPTIONS = 6;

/**
 * Wraps `react-multi-select-component` unchanged (same design/options) and flips its menu to open
 * upward when a row sits too close to the bottom of the AG Grid scroll viewport — otherwise the
 * last rows' menus open downward past the viewport and can't be reached. Only kicks in when the grid
 * is actually scrolling (`ag-layout-normal`); in the auto-height layout the menu already escapes via
 * the grid's overflow rule, so it always opens downward.
 */
export default function FlipMultiSelect(props: MultiSelectProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [flipUp, setFlipUp] = useState(false);

  const handleMenuToggle = (open: boolean) => {
    if (open && wrapRef.current) {
      const scrollViewport = wrapRef.current.closest('.ag-body-viewport') as HTMLElement | null;
      const isScrolling = !!scrollViewport && scrollViewport.scrollHeight > scrollViewport.clientHeight + 2;
      if (isScrolling) {
        const trigger = (wrapRef.current.querySelector('.dropdown-heading') as HTMLElement | null) ?? wrapRef.current;
        const triggerRect = trigger.getBoundingClientRect();
        const viewportRect = scrollViewport.getBoundingClientRect();
        const optionCount = (props.options?.length ?? 0) + 1;
        const estimatedMenuHeight = Math.min(Math.max(optionCount, 1), MAX_VISIBLE_OPTIONS) * OPTION_ROW + MENU_CHROME;
        const spaceBelow = viewportRect.bottom - triggerRect.bottom;
        const spaceAbove = triggerRect.top - viewportRect.top;
        setFlipUp(spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow);
      } else {
        setFlipUp(false);
      }
    } else if (!open) {
      setFlipUp(false);
    }
    props.onMenuToggle?.(open);
  };

  return (
    <div ref={wrapRef} className={flipUp ? 'mssg-ms-flip-up' : undefined} style={{ display: 'contents' }}>
      <MultiSelectWithToggle ArrowRenderer={BombiixArrow} ItemRenderer={BombiixItemRenderer} {...props} onMenuToggle={handleMenuToggle} />
    </div>
  );
}
